import { useCallback, useMemo, useState } from 'react';
import { formatNumber } from 'utils/number';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { Button } from '@carbon/react';
import { usePolling } from 'lib/hooks/usePolling';
import {
  AtomisedNotifications,
  NotificationGroup,
} from 'components/atomised-notifications';
import type { DcaRegressionProgressResponse } from 'types/dca-workspaces';
import {
  activeRegressionRequestAtom,
  regressionProgressAtom,
  cancelRegressionRequestAtom,
  regressionJobCompletedAtom,
  regressionJobFinishedAtom,
} from './atoms';
import styles from './active-job.module.scss';

const POLL_INTERVAL_MS = 10_000;

export const ActiveJob = () => {
  const activeRequest = useAtomValue(activeRegressionRequestAtom);
  const [progress, fetchProgress] = useAtom(regressionProgressAtom);
  const cancelRequest = useSetAtom(cancelRegressionRequestAtom);
  const jobCompleted = useSetAtom(regressionJobCompletedAtom);
  const jobFinished = useAtomValue(regressionJobFinishedAtom);
  const [isCancelling, setIsCancelling] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const progressHref = activeRequest?.resources?.progress?.href ?? null;

  const pollFn = useCallback(async () => {
    if (!progressHref) return;
    try {
      const isDone = await fetchProgress(progressHref);
      if (isDone) jobCompleted();
    } catch {
      // Swallow progress errors so polling continues
    }
  }, [progressHref, fetchProgress, jobCompleted]);

  usePolling({
    fn: pollFn,
    intervalMs: POLL_INTERVAL_MS,
    enabled: !!progressHref && !jobFinished,
    immediate: true,
  });

  const handleCancel = useCallback(async () => {
    setIsCancelling(true);
    try {
      await cancelRequest();
    } catch {
      // Error notification is handled inside the atom
    } finally {
      setIsCancelling(false);
    }
  }, [cancelRequest]);

  if (!activeRequest) return null;

  const jobId = activeRequest.id?.toString() ?? '';
  const percent = Math.round(progress?.percent ?? 0);

  return (
    <>
      <AtomisedNotifications groupIds={[NotificationGroup.DcaActiveJob]} />
      <Accordion
        expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <AccordionSummary>
          <h2 className={styles.sectionHeading}>
            {jobFinished ? 'Completed' : 'Active'} Job - ID {jobId} ({percent}%{' '}
            {jobFinished ? 'done' : 'complete'})
          </h2>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.content}>
            <div className={styles.progressRow}>
              <div className={styles.progressBarWrapper}>
                <div className={styles.progressLabel}>
                  {jobFinished ? 'Completed' : 'Processing...'}
                </div>
                <div className={styles.progressTrack}>
                  <div
                    className={`${styles.progressFill}${
                      jobFinished ? ` ${styles.progressFillDone}` : ''
                    }`}
                    style={{ width: `${progress?.percent ?? 0}%` }}
                  />
                </div>
                <p className={styles.wellsProcessed}>
                  {progress?.processed}/{progress?.total} Wells Processed
                  {jobFinished ? '' : '...'}
                </p>
              </div>

              {!jobFinished && (
                <Button
                  kind="tertiary"
                  size="sm"
                  className={styles.cancelButton}
                  onClick={handleCancel}
                  disabled={isCancelling}
                >
                  {isCancelling ? 'Cancelling…' : 'Cancel Run'}
                </Button>
              )}
            </div>

            <ProgressSummary data={progress} finished={jobFinished} />
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

// ---------------------------------------------------------------------------
// Progress summary sub-component
// ---------------------------------------------------------------------------

const ProgressSummary = ({
  data,
  finished,
}: {
  data: DcaRegressionProgressResponse | null;
  finished: boolean;
}) => {
  const stats = useMemo(() => {
    const items = [
      {
        label: 'Number of Wells requested',
        value: formatNumber(data?.total, 0),
      },
      {
        label: 'Number of Wells complete',
        value: formatNumber(data?.processed, 0),
      },
      {
        label: 'Time elapsed',
        value: `${formatNumber(data?.elapsedSeconds, 1, 1)} s`,
      },
    ];

    if (!finished) {
      items.push({
        label: 'Estimated time remaining',
        value: `${formatNumber(data?.estimatedTime, 1, 1)} s`,
      });
    }

    items.push(
      {
        label: 'Running average time per Well',
        value: `${formatNumber(data?.avgTimePerWell, 2, 2)} s`,
      },
      {
        label: 'Number of errors encountered',
        value: formatNumber(data?.failed, 0),
      }
    );

    return items;
  }, [data, finished]);

  return (
    <div className={styles.summarySection}>
      <h3 className={styles.summaryHeading}>Progress summary</h3>
      <div className={styles.statsGrid}>
        {stats.map((stat) => (
          <div key={stat.label} className={styles.statItem}>
            <span className={styles.statLabel}>{stat.label}</span>
            <span className={styles.statValue}>{stat.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
